import { PermissionFilter } from '../model';
import { AuthService } from './auth.service';

export type Operation = 'Create' | 'Read' | 'Update' | 'Delete' | 'Export' | 'Import';
interface Operations {
  Create: string[];
  Read: string[];
  Update: string[];
  Delete: string[];
  Export: string[];
  Import: string[];
}

class EntityPermissions {
  private readonly permissions: Partial<Operations> = {};

  constructor(permissions: Partial<Operations>) {
    this.permissions = permissions;
  }

  public can(authService: AuthService, ...operations: Operation[]): boolean {
    for (const operation of operations) {
      const required = this.permissions[operation];

      if (!required) {
        continue;
      }

      if (!authService.isSatisfied({ any: required })) {
        return false;
      }
    }

    return true;
  }
}

/**
 * AccessControl - constants and friendly names for permissions 
 * and permission groups
 * 
 * Add more as needed, with reasonable efforts at maintaining some 
 * consistency of naming and grouping. Replace static string usage
 * elsewhere when/as it's convenient.
 */
export class AccessControl {
  // Alerts
  public static readonly AlertAcknowledgment = 'hickory.AcknowledgeReceiptEnable';
  public static readonly AlertChannelEmail = 'hickory.EmailChannel';
  public static readonly AlertChannelEtn = 'hickory.EtnAlertSend';
  public static readonly AlertChannelMobilePush = 'hickory.AlertMobileApp';
  public static readonly AlertChannelPublicLink = 'hickory.public-alert-web-page';
  public static readonly AlertChannelSocialMedia = 'hickory.SocialMediaSend';
  public static readonly AlertChannelTextMessage = 'hickory.TextChannel';
  public static readonly AlertChannelVoice = 'hickory.AlertTelephonyEnable';
  public static readonly AlertFacilityMapSelection = 'hickory.AlertFacilityMapSelection';
  public static readonly AlertSendDefault = 'quick-alert.create';
  public static readonly AlertSendPoll = 'poll.create';
  public static readonly AlertSendPublic = 'hickory.PublicAlert';
  public static readonly AlertVoicePriority = 'hickory.AlertPriorityEnabled';
  public static readonly AlertVoiceWorkflow = 'hickory.AlertWorkflow';
  public static readonly AlertReportExport = 'hickory.AlertReportExport';

  // Contacts and Groups
  public static readonly ContactsView = 'hickory.InternalMembersView';
  public static readonly ContactsEdit = 'hickory.InternalMembersEdit';
  public static readonly ContactsManage = 'hickory.InternalMembersManage';
  public static readonly GroupsManage = 'hickory.InternalGroupsManage';

  // Admins
  public static readonly AdminManage = 'hickory.AdminManage';

  // Miscellaneous
  public static readonly AlertTemplatesManage = 'hickory.AlertTemplatesManage';
  public static readonly RegionManage = 'hickory.RegionManage';
  public static readonly RegionZero = 'hickory.RegionZero';
  public static readonly ScheduledAlertsManage = 'hickory.QuickAlertScheduledManage';
  public static readonly SocialMediaManage = 'hickory.SocialMediaManage';
  public static readonly MobileThemeApi = 'hickory.mobile-theme-api';
  public static readonly ManageSurveys = 'hickory.SurveyModuleManage';
  public static readonly ManageRoles = 'roles.manage';
  public static readonly ManageBrands = 'hickory.BrandingManage';
  public static readonly ViewHandweave = 'hickory.HandweaveViewer';
  public static readonly ManageThirdPartyAuthentication = 'hickory.ThirdPartyAuthenticationsManage';
  public static readonly ViewThirdPartyAuthentication = 'hickory.ThirdPartyAuthenticationsView';
  public static readonly ViewThirdPartyAuthenticationSecrets = 'hickory.ThirdPartyAuthenticationsSecretsView';
  public static readonly ManageKeywords = 'hickory.KeywordsManage';
  public static readonly ManageIntegrations = 'hickory.IntegrationsManage';
  public static readonly ManageSignupConfiguration = 'hickory.PublicSignupConfiguration';
  public static readonly ViewUsageEvents = 'hickory.ViewUsageEvents';
  public static readonly ManageMicrosoftTeams = 'hickory.ManageMicrosoftTeams';
  public static readonly ManagePronunciations = 'hickory.PronunciationManage';
  public static readonly UploadSftp = 'hickory.SftpUpload';
  public static readonly ViewSftpHistory = 'hickory.SftpImportHistory';
  public static readonly ManageSftpConfiguration = 'hickory.SftpConfiguration';
  public static readonly ContentReviewer = 'content-management.contentReviewer';
  public static readonly ContentAuthor = 'content-management.contentAuthor';
  public static readonly CallCenterAccess = 'hickory.OnBehalfOf';

  // Feature Toggles
  public static readonly FeatureAlertBranding = 'branding.send.feature';
  public static readonly FeatureAlertClassification = 'hickory.alert-priority-v2';
  public static readonly FeatureContentImport = 'content-import.feature';
  public static readonly FeatureIncidentManagement = 'hickory.IncidentManagement';
  public static readonly FeatureIncidentManagementNext = 'incident-v2';
  public static readonly FeatureMicrosoftTeams = "hickory.microsoft-teams";
  public static readonly FeatureNavigationBickory = 'hickory.BalsaInlineHickory';   // deprecated
  public static readonly FeatureRecipientsAdHoc = 'hickory.AdHocDistributionList';
  public static readonly FeatureRecipientsDirectories = 'hickory.directories-feature';
  public static readonly FeatureRecipientsIndividual = 'hickory.balsa-individual-recipient';
  public static readonly FeatureRecipientsMunicipalSelection = 'hickory.select-users-by-municipal-area';
  public static readonly FeatureRecipientsSelectionNext = 'recipient-selection-v2.feature';
  public static readonly FeatureRecipientsSplitDirectory = 'hickory.split-hickory-directory-by-classification';
  public static readonly FeatureScheduledAlertsNext = 'hickory.balsa-scheduled-alerts';
  public static readonly FeatureSystemMessageBanner = 'hickory.system-message-banner';
  public static readonly FeatureSurvey = 'hickory.survey-module';
  public static readonly FeatureAdvancedRecipientSearch = 'hickory.advanced-recipient-search';
  public static readonly FeatureMessagePersonalization = 'hickory.use-message-personalization';
  public static readonly FeatureMicrosoftTeamsChannel = 'hickory.microsoft-teams';
  public static readonly FeatureNextdoorChannel = 'hickory.use-nextdoor-channel';
  public static readonly FeatureNextdoorChannelKey = 'UseNextdoorSocialMediaChannel';

  public static readonly FeatureChatbotForethought = 'hickory.ForethoughtChatbot';
  public static readonly FeatureAnySupportChatBot = [this.FeatureChatbotForethought];

  // Composite Permission Groups
  public static readonly CanViewPublicInformation = [this.SocialMediaManage, this.AlertChannelSocialMedia, this.AlertSendPublic, this.AlertChannelEtn];
  public static readonly ContactsRead = [this.ContactsManage, this.ContactsEdit, this.ContactsView];
  public static readonly ContactsUpdate = [this.ContactsManage, this.ContactsEdit];
  public static readonly ContactsDelete = [this.ContactsManage];
  public static readonly ContactsCreate = [this.ContactsManage];
  public static readonly CanManageSurveys = [this.ManageSurveys, this.FeatureSurvey];
  public static readonly CanInvalidateCredentials = ['hickory.InvalidateCredentials', 'hickory.CredentialManage'];


  public static readonly Contacts = new EntityPermissions({
    Create: this.ContactsCreate,
    Read: this.ContactsRead,
    Update: this.ContactsUpdate,
    Delete: this.ContactsDelete,
    Export: [this.ContactsManage],
    Import: [this.ContactsManage],
  });

  public static readonly Groups = new EntityPermissions({
    Create: [this.GroupsManage],
    Read: [this.GroupsManage],
    Update: [this.GroupsManage],
    Delete: [this.GroupsManage],
  });
}

export type AuthZDomain = 'brands' | 'templates' | 'social-media' | 'integrations';
export const BRANDS_AUTHZ_DOMAIN: AuthZDomain = 'brands';
export const TEMPLATES_AUTHZ_DOMAIN: AuthZDomain = 'templates';
export const SOCIAL_AUTHZ_MEDIA_DOMAIN: AuthZDomain = 'social-media';
export const INTEGRATIONS_AUTHZ_DOMAIN: AuthZDomain = 'integrations';

export type AuthZType = 'role' | 'people' | 'group' | 'everyone' | 'everyone-default' | 'custom';

export class AuthZAccessControl {

  // authz actions
  public static readonly readonlyActions = ['read'];
  public static readonly allActions = ['create', 'read', 'update', 'delete'];
  public static isReadonlyOrEmpty(actions?: string[]) {
    return !actions?.length || (actions && actions.length === 1 && actions[0] === 'read');
  }

  // authz permissions
  private static readonly FeatureTemplatesAuthZ = 'hickory.filtered-templates-feature';
  private static readonly FeatureBrandAuthZ = 'hickory:brand-access-control';
  private static readonly FeatureSocialMediaAuthZ = 'social-media-access-control';
  private static readonly FeatureIntegrationsAuthZ = 'integration-access-control';
  private static readonly AuthZEveryoneDefault = 'hickory.everyone-default-authorization';
  private static readonly AuthZEveryone = 'hickory.everyone-authorization';
  // private static readonly AuthZGroup = 'hickory.group-authorization';
  private static readonly AuthZRole = 'hickory.role-authorization';
  private static readonly AuthZPeople = 'hickory.user-authorization';
  private static readonly AuthZCustom = 'hickory.custom-authorization';

  private static readonly FullAccessToBrands = 'hickory.FullAccessToBrands';
  private static readonly FullAccessToTemplates = 'hickory.FullAccessToTemplates';
  private static readonly FullAccessToIntegrations = 'hickory.FullAccessToIntegrations';
  private static readonly FullAccessToSocialMedia = 'hickory.FullAccessToSocialMedia';

  private static readonly NotAuthZEveryoneDefault = [this.AuthZEveryoneDefault];
  private static BuildPermissionFilter(permission: string, ...required: string[]): PermissionFilter {
    return { all: required, any: [permission, AccessControl.RegionZero] };
  }

  /**
   * enforces access to authz types that can be applied
   * user must have either the associated feature toggle or be region 0
   * AND they must have the required Admin Permissions (regardless of region 0)
   */
  private static readonly typePermissions = new Map<AuthZType, PermissionFilter>([
    ['role', this.BuildPermissionFilter(this.AuthZRole, AccessControl.ManageRoles)],
    ['people', this.BuildPermissionFilter(this.AuthZPeople, AccessControl.AdminManage)],
    // ['group', this.BuildPermissionFilter(this.AuthZGroup, AccessControl.GroupsManage)],
    ['everyone', this.BuildPermissionFilter(this.AuthZEveryone, AccessControl.AdminManage)],
    ['everyone-default', { any: [this.AuthZEveryoneDefault], not: [AccessControl.RegionZero] }],
    ['custom', this.BuildPermissionFilter(this.AuthZCustom, AccessControl.ViewHandweave, AccessControl.AdminManage)]
  ]);

  private static readonly domainFullAccessPermissions = new Map<AuthZDomain, string>([
    [BRANDS_AUTHZ_DOMAIN, this.FullAccessToBrands],
    [TEMPLATES_AUTHZ_DOMAIN, this.FullAccessToTemplates],
    [INTEGRATIONS_AUTHZ_DOMAIN, this.FullAccessToIntegrations],
    [SOCIAL_AUTHZ_MEDIA_DOMAIN, this.FullAccessToSocialMedia]
  ]);
  
  private static readonly domainAdminPermissions = new Map<AuthZDomain, string>([
    [BRANDS_AUTHZ_DOMAIN, AccessControl.ManageBrands],
    [TEMPLATES_AUTHZ_DOMAIN, AccessControl.AlertTemplatesManage],
    [INTEGRATIONS_AUTHZ_DOMAIN, AccessControl.ManageIntegrations],
    [SOCIAL_AUTHZ_MEDIA_DOMAIN, AccessControl.SocialMediaManage]
  ]);

  /**
   * enforces access modify authz within domains
   * user must have either the associated feature toggle or be region 0
   * AND they must have the required Admin Permissions (regardless of region 0)
   */
  private static readonly domainPermissions = new Map<AuthZDomain, PermissionFilter>([
    [BRANDS_AUTHZ_DOMAIN, this.BuildPermissionFilter(this.FeatureBrandAuthZ, AccessControl.ManageBrands)],
    [TEMPLATES_AUTHZ_DOMAIN, this.BuildPermissionFilter(this.FeatureTemplatesAuthZ, AccessControl.AlertTemplatesManage)],
    [INTEGRATIONS_AUTHZ_DOMAIN, this.BuildPermissionFilter(this.FeatureIntegrationsAuthZ, AccessControl.ManageIntegrations)],
    [SOCIAL_AUTHZ_MEDIA_DOMAIN, this.BuildPermissionFilter(this.FeatureSocialMediaAuthZ, AccessControl.AlertTemplatesManage)]
  ]);

  public static canChangeOwner(auth: AuthService, domain: AuthZDomain) {
    return auth.isSuperAdmin() || (
      this.domainFullAccessPermissions.has(domain) && auth.hasPermission(this.domainFullAccessPermissions.get(domain))
    );
  }

  private static everyOneDefaultDisabled(auth: AuthService) {
    // if not super admin, make sure they don't have this feature toggle enabled
    if (!auth.isSuperAdmin()) {
      return auth.isSatisfied({ not: this.NotAuthZEveryoneDefault });
    }
    return auth.isSuperAdmin();
  }

  public static canAccessAuthZDomain(auth: AuthService, domain: AuthZDomain) {
    if (this.domainPermissions.has(domain)) {
      const canAccessDomain = auth.isSatisfied(this.domainPermissions.get(domain));
      let hasAccessToOneType = false;
      for (const type of this.typePermissions.keys()) {
        if (this.canAccessAuthZType(auth, type)) {
          hasAccessToOneType = true;
          break;
        }
      }
      return canAccessDomain && hasAccessToOneType && this.everyOneDefaultDisabled(auth);
    }
    throw Error(`Access control is not enabled for ${domain}`);
  }

  public static canAccessAuthZType(auth: AuthService, type: AuthZType) {
    if (this.typePermissions.has(type)) {
      // role is weird and we only get role permissions back when role mgmt is on for the tenant
      // since back office will not have the feature toggle enabled, we gotta get hacky
      if (auth.isSuperAdmin() && type === 'role') return true;
      else return auth.isSatisfied(this.typePermissions.get(type)) && this.everyOneDefaultDisabled(auth);
    }
    throw Error(`Access control is not enabled for ${type}`);
  }

  public static getActionsFromEntity<T extends { actions?: string[] }>(auth: AuthService, domain: AuthZDomain, entity: T): string[] {
    // if we have the entity, return its actions
    if (entity && entity.actions) return entity.actions.map(x => x.toLowerCase());
    // otherwise fallback to the manage permission for the domain
    if (!entity || !entity.actions || !this.canAccessAuthZDomain(auth, domain)) {
      if (auth.isSatisfied(this.domainPermissions.get(domain)) || auth.hasPermission(this.domainAdminPermissions.get(domain))) {
        return this.allActions;
      }
      return [];
    }
  }
}